import { useState, useEffect, useRef } from 'react';
import WaveSurfer from 'wavesurfer.js';
import HoverPlugin from 'wavesurfer.js/dist/plugins/hover.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.js';

export const useCallAudioPlayer = (audioUrl: string) => {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (waveformRef.current) {
      waveSurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#ccc',
        progressColor: '#fb9318',
        barWidth: 2,
        barAlign: 'bottom',
        plugins: [
          HoverPlugin.create({
            lineColor: '#fb9318',
            lineWidth: 2,
            labelBackground: '#555',
            labelColor: '#fff',
            labelPreferLeft: false,
          }),
          TimelinePlugin.create(),
        ],
      });

      waveSurferRef.current.load(audioUrl);

      waveSurferRef.current.on('load', () => {
        setIsLoading(true);
      });

      waveSurferRef.current.on('ready', () => {
        setIsLoading(false);
        togglePlayPause();
      });

      waveSurferRef.current.on('finish', () => {
        setIsPlaying(false);
      });

      waveSurferRef.current.on('timeupdate', (newTime) => {
        setCurrentTimeSeconds(Math.floor(newTime));
      });

      waveSurferRef.current.on('ready', () => {
        setDurationSeconds(waveSurferRef.current?.getDuration() || 0);
      });
    }

    return () => {
      waveSurferRef.current?.destroy();
    };
  }, [audioUrl]);

  const setPlayerVolume = (newVolume: number) => {
    waveSurferRef.current?.setVolume(newVolume / 100);
  };

  const togglePlayPause = () => {
    if (!waveSurferRef.current) return;
    waveSurferRef.current.playPause();
    setIsPlaying(waveSurferRef.current.isPlaying());
  };

  const stop = () => {
    waveSurferRef.current?.stop();
    setIsPlaying(false);
  };

  const rewind = () => {
    const current = waveSurferRef.current?.getCurrentTime() || 0;
    waveSurferRef.current?.seekTo(
      Math.max((current - 5) / waveSurferRef.current.getDuration(), 0),
    );
  };

  const forward = () => {
    const current = waveSurferRef.current?.getCurrentTime() || 0;
    waveSurferRef.current?.seekTo(Math.min((current + 5) / durationSeconds, 1));
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    const newVolume = isMuted ? 0 : volume;
    setPlayerVolume(newVolume);
  };

  const handleVolumeChange = (newVolume: number) => {
    if (newVolume === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
    setVolume(newVolume);
    setPlayerVolume(newVolume);
  };

  const seekTo = (time: number) => {
    waveSurferRef.current?.setTime(time);
  };

  return {
    waveformRef,
    waveSurferRef,
    isPlaying,
    volume,
    isMuted,
    currentTimeSeconds,
    durationSeconds,
    isLoading,
    togglePlayPause,
    stop,
    rewind,
    forward,
    toggleMute,
    handleVolumeChange,
    setCurrentTimeSeconds: seekTo,
  };
};
